
/**
 * RiskRegister Service Layer
 * Rule: Business logic, validation, and API orchestration
 * Rule: NO UI IN SDK
 */
import { ApiError } from '../errors/api-error.js';
import { withRetry } from '../utils/retry.js';

export class RiskRegisterService {
    constructor(api, config = {}) {
        this.api = api;
        this.config = config;
    }

    /**
     * Get all items with pagination
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>}
     */
    async getAll(params = {}) {
        try {
            const response = await withRetry(
                () => this.api.getAll(params),
                { maxRetries: 2 }
            );

            return {
                success: true,
                data: response.data,
                meta: response.pagination || response.meta || {},
                uniqueValues: response.uniqueValues || {},
                message: response.message || 'RiskRegister listesi getirildi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch risk-register list');
        }
    }

    async createFilteredReportViewerSession(payload) {
        try {
            return await this.api.createFilteredReportViewerSession(payload);
        } catch (error) {
            throw this._handleError(error, 'Failed to create filtered risk report viewer session');
        }
    }


    async exportFilteredReport(payload) {
        try {
            return await this.api.exportFilteredReport(payload);
        } catch (error) {
            throw this._handleError(error, 'Failed to export filtered risk report');
        }
    }

    /**
     * Get item by ID
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async getById(id) {
        try {
            const response = await withRetry(
                () => this.api.getById(id),
                { maxRetries: 2 }
            );

            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch risk-register');
        }
    }

    /**
     * Create new item
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async create(data) {
        const payload = data;

        try {
            const response = await this.api.create(payload);

            return {
                success: true,
                data: response.data,
                message: response.message || 'RiskRegister oluşturuldu'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to create risk-register');
        }
    }

    /**
     * Update item
     * @param {number|string} id
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async update(id, data) {
        const payload = data;

        try {
            const response = await this.api.update(id, payload);

            return {
                success: true,
                data: response.data,
                message: response.message || 'RiskRegister güncellendi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to update risk-register');
        }
    }

    /**
     * Delete item
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async delete(id) {
        try {
            const response = await this.api.delete(id);

            return {
                success: true,
                data: response.data,
                message: response.message || 'RiskRegister silindi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to delete risk-register');
        }
    }

    /**
     * Bulk delete items
     * @param {Array<number|string>} ids
     * @returns {Promise<Object>}
     */
    async bulkDelete(ids) {
        try {
            const response = await this.api.batchDelete(ids);

            return {
                success: true,
                data: response.data,
                message: response.message || `${ids.length} öğe silindi`
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to bulk delete risk-registers');
        }
    }

    /**
     * Custom operation: createBatchDelete
     */
    async createBatchDelete(data = {}) {
        try {
            const response = await this.api.client.post(`${this.api.basePath}/batch-delete`, data);
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to execute createBatchDelete');
        }
    }

    /**
     * Get Controls for Risk Register
     */
    async getControls(id, params = {}) {
        try {
            const response = await this.api.getControls(id, params);
            return {
                success: true,
                data: response.data,
                meta: response.meta || response.pagination || {},
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch existing controls');
        }
    }

    /**
     * Get paginated treatments for a Risk Register
     */
    async getRelatedTreatments(id, params = {}) {
        try {
            const response = await this.api.getRelatedTreatments(id, params);
            return {
                success: true,
                data: response.data,
                meta: response.meta || response.pagination || {},
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch related treatments');
        }
    }

    /**
     * Add Controls to Risk Register
     */
    async addControls(id, controlIds) {
        try {
            const response = await this.api.addControls(id, controlIds);
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to add controls');
        }
    }

    /**
     * Remove Control from Risk Register
     */
    async removeControl(id, controlId) {
        try {
            const response = await this.api.removeControl(id, controlId);
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to remove control');
        }
    }

    /**
     * Add Treatments to Risk Register
     */
    async addTreatments(id, treatmentIds) {
        try {
            const response = await this.api.addTreatments(id, treatmentIds);
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to add treatments');
        }
    }

    /**
     * Remove Treatment from Risk Register
     */
    async removeTreatment(id, treatmentId) {
        try {
            const response = await this.api.removeTreatment(id, treatmentId);
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to remove treatment');
        }
    }

    async getRelatedCauses(id, params = {}) {
        try {
            const response = await this.api.getRelatedCauses(id, params);
            return {
                success: true,
                data: response.data,
                meta: response.meta || response.pagination || {},
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch related causes');
        }
    }

    async addCauses(id, rootCauseIds) {
        try {
            const response = await this.api.addCauses(id, rootCauseIds);
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to add causes');
        }
    }

    async removeCause(id, rootCauseId) {
        try {
            const response = await this.api.removeCause(id, rootCauseId);
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to remove cause');
        }
    }

    async getRelatedConsequences(id, params = {}) {
        try {
            const response = await this.api.getRelatedConsequences(id, params);
            return {
                success: true,
                data: response.data,
                meta: response.meta || response.pagination || {},
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch related consequences');
        }
    }

    async addConsequences(id, consequenceIds) {
        try {
            const response = await this.api.addConsequences(id, consequenceIds);
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to add consequences');
        }
    }

    async removeConsequence(id, consequenceId) {
        try {
            const response = await this.api.removeConsequence(id, consequenceId);
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to remove consequence');
        }
    }

    async getRelatedAssets(id, params = {}) {
        try {
            const response = await this.api.getRelatedAssets(id, params);
            return {
                success: true,
                data: response.data,
                meta: response.meta || response.pagination || {},
                message: response.message,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch related assets');
        }
    }

    async linkAssets(id, assetIds) {
        try {
            const response = await this.api.linkAssets(id, assetIds);
            return {
                success: true,
                data: response.data,
                message: response.message,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to link assets');
        }
    }

    async unlinkAsset(id, assetId) {
        try {
            const response = await this.api.unlinkAsset(id, assetId);
            return {
                success: true,
                data: response.data,
                message: response.message,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to unlink asset');
        }
    }

    async unlinkAssetsBulk(id, assetIds) {
        try {
            const response = await this.api.unlinkAssetsBulk(id, assetIds);
            return {
                success: true,
                data: response.data,
                message: response.message,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to unlink assets');
        }
    }

    async unlinkAssetSource(id, assetId, payload) {
        try {
            const response = await this.api.unlinkAssetSource(id, assetId, payload);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to unlink asset source');
        }
    }

    async linkAssetsByCategory(id, assetCategoryId) {
        try {
            const response = await this.api.linkAssetsByCategory(id, assetCategoryId);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to link assets by category');
        }
    }

    async unlinkAssetsByCategory(id, assetCategoryId) {
        try {
            const response = await this.api.unlinkAssetsByCategory(id, assetCategoryId);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to unlink assets by category');
        }
    }

    async linkAssetsByProcess(id, processId) {
        try {
            const response = await this.api.linkAssetsByProcess(id, processId);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to link assets by process');
        }
    }

    async unlinkAssetsByProcess(id, processId) {
        try {
            const response = await this.api.unlinkAssetsByProcess(id, processId);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to unlink assets by process');
        }
    }

    async getRelatedBias(id, params = {}) {
        try {
            const response = await this.api.getRelatedBias(id, params);
            return {
                success: true,
                data: response.data || [],
                pagination: response.pagination || {},
                message: response.message,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch related BIAs');
        }
    }

    async addBias(id, biaIds) {
        try {
            const response = await this.api.addBias(id, biaIds);
            return {
                success: true,
                data: response.data,
                message: response.message,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to link BIAs');
        }
    }

    async removeBia(id, biaId) {
        try {
            const response = await this.api.removeBia(id, biaId);
            return {
                success: true,
                data: response.data,
                message: response.message,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to unlink BIA');
        }
    }

    async getRelatedObjectives(id, params = {}) {
        try {
            const response = await this.api.getRelatedObjectives(id, params);
            return {
                success: true,
                data: response.data || [],
                pagination: response.pagination || {},
                message: response.message,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch related objectives');
        }
    }

    async addObjectives(id, objectiveIds) {
        try {
            const response = await this.api.addObjectives(id, objectiveIds);
            return {
                success: true,
                data: response.data,
                message: response.message,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to link objectives');
        }
    }

    async removeObjective(id, objectiveId) {
        try {
            const response = await this.api.removeObjective(id, objectiveId);
            return {
                success: true,
                data: response.data,
                message: response.message,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to unlink objective');
        }
    }

    /**
     * Get statistics
     */
    async getStats() {
        try {
            const response = await this.api.getStats();
            return {
                success: true,
                data: response.data
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch statistics');
        }
    }

    async getPartyTemplate(params = {}) {
        try {
            const response = await withRetry(
                () => this.api.getPartyTemplate(params),
                { maxRetries: 2 }
            );
            return {
                success: response.success !== false,
                data: response.data,
                message: response.message,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch risk party template');
        }
    }

    async getParty(id) {
        try {
            const response = await withRetry(
                () => this.api.getParty(id),
                { maxRetries: 2 }
            );
            return {
                success: response.success !== false,
                data: response.data,
                partyTemplate: response.partyTemplate ?? null,
                message: response.message,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch risk party');
        }
    }

    async syncParty(id, payload) {
        try {
            const response = await this.api.syncParty(id, payload);
            return {
                success: response.success !== false,
                data: response.data,
                message: response.message,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to sync risk party');
        }
    }

    /**
     * Handle and transform errors
     */
    _handleError(error, defaultMessage) {
        if (error instanceof ApiError) {
            throw error;
        }

        if (error.statusCode || error.status) {
            throw ApiError.fromResponse(error);
        }

        throw new ApiError(error.message || defaultMessage, 500, 'UNKNOWN_ERROR', error);
    }
}
